import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  styled,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { OrganizationSubscriptionTab } from '../../components/subscriptions/OrganizationSubscriptionTab';

interface OrganizationFormData {
  name: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
}

const SectionCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(to bottom right, #ffffff, #fafbfc)',
  borderRadius: '16px',
  boxShadow: '0 2px 16px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const OrganizationEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<OrganizationFormData>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['organization', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/organizations/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        address: data.address || '',
      });
    }
  }, [data, reset]);

  const updateMutation = useMutation({
    mutationFn: (formData: any) => axiosInstance.patch(`/organizations/${id}`, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', id] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      showMessage('Organization updated successfully', 'success');
      navigate('/organizations');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update organization', 'error');
    },
  });

  const onSubmit = (formData: OrganizationFormData) => {
    updateMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load organization'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Edit Organization
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              {...register('name', { required: 'Organization name is required' })}
              error={!!errors.name}
              helperText={errors.name?.message}
              margin="normal"
              fullWidth
              label="Organization Name"
            />

            <TextField
              {...register('contactEmail', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={!!errors.contactEmail}
              helperText={errors.contactEmail?.message}
              margin="normal"
              fullWidth
              label="Contact Email"
              type="email"
            />

            <TextField
              {...register('contactPhone', { required: 'Phone is required' })}
              error={!!errors.contactPhone}
              helperText={errors.contactPhone?.message}
              margin="normal"
              fullWidth
              label="Contact Phone"
            />

            <TextField
              {...register('address')}
              margin="normal"
              fullWidth
              label="Address"
              multiline
              rows={3}
            />

            <Box display="flex" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/organizations')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Subscription Section */}
      <SectionCard sx={{ mt: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <SectionHeader>
            <IconBox>
              <CreditCardIcon sx={{ color: '#fff', fontSize: 24 }} />
            </IconBox>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: '#1a1f36',
                  fontWeight: 700,
                  fontSize: '18px',
                }}
              >
                Subscription Plan
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#697386',
                  fontSize: '14px',
                }}
              >
                Manage the subscription plan for this organization
              </Typography>
            </Box>
          </SectionHeader>
          <Divider sx={{ mb: 3 }} />
          {data && (
            <OrganizationSubscriptionTab
              organizationId={id!}
              organizationName={data.name}
            />
          )}
        </CardContent>
      </SectionCard>
    </Box>
  );
};
